import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash } from '../../ui/kit.js';
import { RESIDENT, printDate, nextId } from '../../state/building.js';

import FitnessCenter from '../../media/images/modules/fitness-center.jpg';
import TennisCourt from '../../media/images/modules/tennis-court.jpg';
import PartyRoom from '../../media/images/modules/party-room.jpg';
import PoolCabana from '../../media/images/modules/pool-cabana.jpg';
import GolfSimulator from '../../media/images/modules/golf-simulator.jpg';
import GuestSuite from '../../media/images/modules/guest-suite.jpg';

const HOURS = ['7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM'];

const AMENITIES = {
    'Fitness center': { place: 'Level 5 wellness deck', slots: HOURS, image: FitnessCenter },
    'Tennis court': { place: 'Rooftop court', slots: ['7:00 AM', '8:30 AM', '10:00 AM', '4:00 PM', '5:30 PM', '7:00 PM'], image: TennisCourt },
    'Party room': { place: 'Bayview lounge, level 2', slots: ['11:00 AM – 3:00 PM', '4:00 PM – 8:00 PM', '8:00 PM – 11:00 PM'], image: PartyRoom },
    'Pool cabana': { place: 'Oceanfront pool deck', slots: ['9:00 AM – 1:00 PM', '1:00 PM – 5:00 PM', '5:00 PM – 8:00 PM'], image: PoolCabana },
    'Golf simulator': { place: 'Club room, level 2', slots: HOURS, image: GolfSimulator },
    'Guest suite': { place: 'Suite 301', slots: ['Check-in 3:00 PM, 1 night', 'Check-in 3:00 PM, 2 nights', 'Check-in 3:00 PM, 3 nights'], image: GuestSuite }
};

/* A date input's value, `offset` days from today. */
function iso(offset = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* Other residents' bookings: the same slots read taken every visit. */
function takenByOthers(amenity, date, slot) {
    const text = `${amenity}${date}${slot}`;
    let hash = 0;
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) % 997;
    return hash % 3 === 0;
}

const SEED = [
    { id: 1, amenity: 'Pool cabana', place: AMENITIES['Pool cabana'].place, date: iso(2), slot: '1:00 PM – 5:00 PM' },
    { id: 2, amenity: 'Tennis court', place: AMENITIES['Tennis court'].place, date: iso(4), slot: '7:00 AM' }
];

/* Amenities / Reservations: pick an amenity and a day, tap an open slot. */
function ReservationsScreen() {
    const [flash, notify] = useFlash();
    const [amenity, setAmenity] = useState('Fitness center');
    const [date, setDate] = useState(iso(0));
    const [bookings, setBookings] = useState(SEED);

    const mine = slot => bookings.some(b => b.amenity === amenity && b.date === date && b.slot === slot);

    const book = slot => {
        setBookings(list => [...list, { id: nextId(), amenity, place: AMENITIES[amenity].place, date, slot }]);
        notify(`${amenity} booked for ${printDate(date)}, ${slot}. See you there, ${RESIDENT.name.split(' ')[0]}.`);
    };

    const cancel = booking => {
        setBookings(list => list.filter(b => b.id !== booking.id));
        notify(`${booking.amenity} on ${printDate(booking.date)} cancelled. The slot is open again.`);
    };

    const sorted = [...bookings].sort((a, b) => a.date.localeCompare(b.date));

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Amenities" title="Book an amenity" />
            {flash}

            <div className="stack">
                <div className="tiles" role="group" aria-label="Amenity">
                    {Object.keys(AMENITIES).map(name => (
                        <button type="button" className="tile tile--photo" key={name} aria-pressed={amenity === name} onClick={() => setAmenity(name)}>
                            <img src={AMENITIES[name].image} alt="" loading="lazy" />
                            <span>{name}</span>
                        </button>
                    ))}
                </div>

                <div className="split">
                    <Panel className="panel--accent">
                        <img className="panel__media" src={AMENITIES[amenity].image} alt={amenity} loading="lazy" />
                        <h4>{amenity}</h4>
                        <p className="row__meta">{AMENITIES[amenity].place}</p>
                        <div className="form">
                            <Field label="Date" wide>
                                <input type="date" className="form-control" value={date} min={iso(0)} onChange={e => setDate(e.target.value || iso(0))} />
                            </Field>
                        </div>
                        <div className="tiles">
                            {AMENITIES[amenity].slots.map(slot => {
                                const yours = mine(slot);
                                const taken = !yours && takenByOthers(amenity, date, slot);

                                return (
                                    <button type="button" className="tile" key={slot} disabled={taken || yours} onClick={() => book(slot)} style={taken ? { opacity: 0.5 } : undefined}>
                                        <span>{slot}</span>
                                        {yours ? <Chip tone="info">Yours</Chip> : taken ? <Chip tone="muted">Taken</Chip> : <Chip tone="ok">Open</Chip>}
                                    </button>
                                );
                            })}
                        </div>
                    </Panel>

                    <div className="stack">
                        <h4>Your reservations</h4>
                        {sorted.length === 0 ? <Empty>No reservations yet. Tap an open slot to book one.</Empty> : (
                            <ul className="rows">
                                {sorted.map(b => (
                                    <li className="row" key={b.id}>
                                        <div className="row__main">
                                            <span className="row__title">{b.amenity}</span>
                                            <span className="row__meta">{printDate(b.date)} · {b.slot} · {b.place}</span>
                                        </div>
                                        <div className="row__end">
                                            <Chip tone="ok">Confirmed</Chip>
                                            <button type="button" className="btn btn-outline-light btn-sm" onClick={() => cancel(b)}>Cancel</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReservationsScreen;
